import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Mail, Calendar as CalendarIcon, Clock, MessageSquare, Inbox, RefreshCw, CalendarClock, XCircle, X, Check, Video } from 'lucide-react';
import { Calendar } from '../components/ui/calendar';
import { es as esLocale } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLASS_TYPE_LABELS = {
  prueba: 'Clase de Prueba (30 min)',
  individual: 'Clase Individual (55 min)',
  pack5: 'Pack 5 clases',
  pack10: 'Pack 10 clases',
  dele: 'Preparación DELE (60 min)',
  intensivo: 'Paquete Intensivo',
};

const CLASS_TYPE_COLORS = {
  prueba: 'bg-[#E8F5FA] text-[#7DBDD9] border-[#A8D8EA]',
  individual: 'bg-[#FFF0EF] text-[#E38987] border-[#F5A6A4]',
  pack5: 'bg-purple-50 text-purple-600 border-purple-200',
  pack10: 'bg-indigo-50 text-indigo-600 border-indigo-200',
  dele: 'bg-amber-50 text-amber-700 border-amber-200',
  intensivo: 'bg-purple-50 text-purple-600 border-purple-200',
};

const STATUS_BADGES = {
  pending: { label: 'Pendiente', cls: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  rescheduled: { label: 'Reprogramada', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  cancelled: { label: 'Cancelada', cls: 'bg-red-50 text-red-700 border-red-200' },
};

const formatLocalDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

function RescheduleModal({ booking, onClose, onDone, authHeaders }) {
  const [availability, setAvailability] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/availability`).then(({ data }) => setAvailability(data));
  }, []);

  const availableTimes = useMemo(() => {
    if (!selectedDate || !availability) return [];
    const key = String(selectedDate.getDay());
    const cfg = availability.weekly?.[key];
    const dateStr = formatLocalDate(selectedDate);
    // Horas de la semana + horas abiertas puntualmente para ese día
    const opened = availability.opened_slots?.[dateStr] || [];
    const base = cfg?.enabled ? (cfg.slots || []) : [];
    const merged = Array.from(new Set([...base, ...opened])).sort();
    const taken = new Set([
      ...(availability.booked_slots?.[dateStr] || []),
      ...(availability.blocked_slots?.[dateStr] || []),
    ]);
    // Allow keeping the same slot (in case admin only changes date)
    if (booking && booking.date === dateStr) taken.delete(booking.time);
    return merged.filter((s) => !taken.has(s));
  }, [selectedDate, availability, booking]);

  const isDateDisabled = (date) => {
    if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;
    if (!availability) return true;
    const dateStr = formatLocalDate(date);
    if ((availability.blocked_dates || []).includes(dateStr)) return true;
    const cfg = availability.weekly?.[String(date.getDay())];
    const weeklyEnabled = cfg?.enabled && (cfg.slots || []).length > 0;
    const hasOpened = (availability.opened_slots?.[dateStr] || []).length > 0;
    return !weeklyEnabled && !hasOpened;
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Selecciona fecha y hora');
      return;
    }
    setSaving(true);
    try {
      await axios.patch(
        `${API}/bookings/${booking.id}/reschedule`,
        { date: formatLocalDate(selectedDate), time: selectedTime },
        authHeaders
      );
      toast.success('Reserva reprogramada. Se ha notificado al alumno.');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al reprogramar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="reschedule-modal"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl p-6 max-w-lg w-full shadow-2xl my-8"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-[#4A4A4A]">Reprogramar clase</h3>
            <p className="text-sm text-gray-500 mt-1">
              {booking.name} · Actualmente: <strong>{booking.date}</strong> a las <strong>{booking.time}</strong>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="reschedule-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-gray-50 rounded-2xl p-3 mb-4">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(d) => { setSelectedDate(d); setSelectedTime(null); }}
            disabled={isDateDisabled}
            locale={esLocale}
            className="rounded-md border-0 mx-auto"
          />
        </div>

        {selectedDate && (
          <div className="mb-4">
            <p className="text-sm font-semibold text-[#4A4A4A] mb-2">Nueva hora:</p>
            <div className="grid grid-cols-4 gap-2">
              {availableTimes.map((t) => (
                <button
                  key={t}
                  onClick={() => setSelectedTime(t)}
                  data-testid={`reschedule-time-${t}`}
                  className={`py-2 rounded-lg text-sm font-medium transition-all ${
                    selectedTime === t
                      ? 'bg-[#7DBDD9] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            {availableTimes.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-2">No hay horas disponibles ese día.</p>
            )}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={saving || !selectedDate || !selectedTime}
          data-testid="reschedule-confirm-btn"
          className="w-full py-3 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-bold shadow disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Reprogramando…</> : <><Check className="w-4 h-4" /> Confirmar reprogramación</>}
        </button>
      </motion.div>
    </motion.div>
  );
}

export default function AdminBookings({ onSessionExpired }) {
  const { token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [changingTypeId, setChangingTypeId] = useState(null);
  const [meetId, setMeetId] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookings?limit=200`, authHeaders);
      setBookings(data);
    } catch (e) {
      if (e.response?.status === 401) {
        onSessionExpired && onSessionExpired();
      } else {
        toast.error('Error al cargar las reservas');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatCreatedAt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) +
        ' ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso;
    }
  };

  const handleCancel = async (b) => {
    if (!window.confirm(`¿Cancelar la clase de ${b.name} del ${b.date} a las ${b.time}?\n\nSe eliminará el evento del calendario y se notificará al alumno.`)) return;
    setCancellingId(b.id);
    try {
      await axios.post(`${API}/bookings/${b.id}/cancel`, {}, authHeaders);
      toast.success('Reserva cancelada. Se ha notificado al alumno.');
      fetchBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cancelar');
    } finally {
      setCancellingId(null);
    }
  };

  const handleClassTypeChange = async (b, newType) => {
    if (newType === b.class_type) return;
    setChangingTypeId(b.id);
    try {
      await axios.patch(`${API}/bookings/${b.id}/class-type`, { class_type: newType }, authHeaders);
      setBookings((prev) => prev.map((x) => (x.id === b.id ? { ...x, class_type: newType } : x)));
      toast.success(`Tipo de clase actualizado a "${CLASS_TYPE_LABELS[newType]}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cambiar el tipo de clase');
    } finally {
      setChangingTypeId(null);
    }
  };

  const handleMeet = async (b) => {
    setMeetId(b.id);
    try {
      const { data } = await axios.post(`${API}/bookings/${b.id}/meet`, {}, authHeaders);
      setBookings((prev) => prev.map((x) => (x.id === b.id ? { ...x, meet_link: data.meet_link } : x)));
      toast.success('Enlace de Google Meet generado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar el enlace');
    } finally {
      setMeetId(null);
    }
  };

  const handleResend = async (b) => {
    setResendingId(b.id);
    try {
      await axios.post(`${API}/bookings/${b.id}/resend-notification`, {}, authHeaders);
      toast.success('Avisos reenviados: a ti y al alumno.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al reenviar');
    } finally {
      setResendingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-10 h-10 text-[#7DBDD9] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#4A4A4A] mb-1">Reservas recibidas</h2>
          <p className="text-gray-600">{bookings.length} {bookings.length === 1 ? 'reserva' : 'reservas'} en total</p>
        </div>
        <button
          onClick={fetchBookings}
          data-testid="refresh-bookings-btn"
          className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-[#4A4A4A] hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Actualizar</span>
        </button>
      </div>

      {bookings.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-100" data-testid="no-bookings-message">
          <Inbox className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">Aún no tienes reservas</p>
          <p className="text-gray-400 text-sm mt-1">Cuando un estudiante reserve una clase, aparecerá aquí.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((b, index) => {
            const statusBadge = STATUS_BADGES[b.status];
            const isCancelled = b.status === 'cancelled';
            return (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.04 }}
                data-testid={`booking-card-${index}`}
                className={`bg-white rounded-2xl border shadow-sm p-5 transition-shadow ${isCancelled ? 'border-red-100 opacity-70' : 'border-gray-100 hover:shadow-md'}`}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <span className="w-10 h-10 bg-gradient-to-br from-[#A8D8EA] to-[#7DBDD9] rounded-full flex items-center justify-center text-white font-bold">
                        {b.name?.[0]?.toUpperCase() || '?'}
                      </span>
                      <span className="font-bold text-[#4A4A4A] text-lg">{b.name}</span>
                      {isCancelled ? (
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${CLASS_TYPE_COLORS[b.class_type] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                          {CLASS_TYPE_LABELS[b.class_type] || b.class_type}
                        </span>
                      ) : (
                        <select
                          value={b.class_type}
                          onChange={(e) => handleClassTypeChange(b, e.target.value)}
                          disabled={changingTypeId === b.id}
                          data-testid={`class-type-select-${index}`}
                          title="Corregir el tipo de clase de esta reserva"
                          className={`px-3 py-1 rounded-full text-xs font-semibold border cursor-pointer outline-none disabled:opacity-60 ${CLASS_TYPE_COLORS[b.class_type] || 'bg-gray-100 text-gray-600 border-gray-200'}`}
                        >
                          {Object.entries(CLASS_TYPE_LABELS).map(([k, label]) => (
                            <option key={k} value={k}>{label}</option>
                          ))}
                        </select>
                      )}
                      <span className="px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-500 uppercase">
                        {b.language}
                      </span>
                      {statusBadge && (
                        <span className={`px-2 py-1 rounded-md text-xs font-semibold border ${statusBadge.cls}`}>
                          {statusBadge.label}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-600 ml-1">
                      <a href={`mailto:${b.email}`} className="flex items-center gap-1.5 hover:text-[#7DBDD9]">
                        <Mail className="w-4 h-4" />{b.email}
                      </a>
                      <span className={`flex items-center gap-1.5 ${isCancelled ? 'line-through' : ''}`}>
                        <CalendarIcon className="w-4 h-4 text-[#A8D8EA]" />{b.date}
                      </span>
                      <span className={`flex items-center gap-1.5 ${isCancelled ? 'line-through' : ''}`}>
                        <Clock className="w-4 h-4 text-[#A8D8EA]" />{b.time}
                      </span>
                    </div>
                    {b.message && (
                      <div className="mt-3 flex items-start gap-2 text-sm text-gray-600 bg-gray-50 rounded-xl p-3">
                        <MessageSquare className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                        <span>{b.message}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="text-xs text-gray-400 md:text-right whitespace-nowrap">
                      Reservado el<br />{formatCreatedAt(b.created_at)}
                    </div>
                    {!isCancelled && (
                      <div className="flex flex-wrap justify-end gap-2 mt-1">
                        {b.meet_link ? (
                          <a
                            href={b.meet_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`meet-link-${index}`}
                            title="Abrir la videollamada de esta clase"
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold transition-colors"
                          >
                            <Video className="w-3.5 h-3.5" />
                            Google Meet
                          </a>
                        ) : (
                          <button
                            onClick={() => handleMeet(b)}
                            disabled={meetId === b.id}
                            data-testid={`meet-btn-${index}`}
                            title="Generar el enlace de Google Meet para esta clase"
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-600 text-xs font-semibold transition-colors disabled:opacity-50"
                          >
                            {meetId === b.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Video className="w-3.5 h-3.5" />
                            )}
                            Generar Meet
                          </button>
                        )}
                        <button
                          onClick={() => handleResend(b)}
                          disabled={resendingId === b.id}
                          data-testid={`resend-btn-${index}`}
                          title="Reenviar el aviso por email a ti y al alumno"
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FFF0EF] hover:bg-[#F5A6A4]/40 text-[#E38987] text-xs font-semibold transition-colors disabled:opacity-50"
                        >
                          {resendingId === b.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Mail className="w-3.5 h-3.5" />
                          )}
                          Reenviar aviso
                        </button>
                        <button
                          onClick={() => setRescheduleTarget(b)}
                          data-testid={`reschedule-btn-${index}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E8F5FA] hover:bg-[#A8D8EA]/40 text-[#7DBDD9] text-xs font-semibold transition-colors"
                        >
                          <CalendarClock className="w-3.5 h-3.5" />
                          Reprogramar
                        </button>
                        <button
                          onClick={() => handleCancel(b)}
                          disabled={cancellingId === b.id}
                          data-testid={`cancel-btn-${index}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold transition-colors disabled:opacity-50"
                        >
                          {cancellingId === b.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <XCircle className="w-3.5 h-3.5" />
                          )}
                          Cancelar
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {rescheduleTarget && (
          <RescheduleModal
            booking={rescheduleTarget}
            onClose={() => setRescheduleTarget(null)}
            onDone={() => { setRescheduleTarget(null); fetchBookings(); }}
            authHeaders={authHeaders}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
